import React, { useState } from 'react';

import { Link, useLocation } from '@remix-run/react';
import { Logo } from '~/components/images/Logo';
import { Button } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { cn } from '~/lib/utils';
import Login from '~/routes/_index/login';
import Registration from '~/routes/_index/registration';
import { validateUser } from '~/services/api/auth';
import { SystemRoles } from '~/types';
import { ChevronDown, CircleUser, List } from 'lucide-react';

export interface HeaderProps {
  user: NonNullable<Awaited<ReturnType<typeof validateUser>>['user']>;
}

const categoriesLinks = [
  {
    label: 'Auctions',
    href: '/auctions',
  },
  {
    label: 'All Products',
    href: '/products',
  },
  {
    label: 'Art',
    href: `/products?category=Art`,
  },
  {
    label: 'Antiques',
    href: `/products?category=Antiques`,
  },
  {
    label: 'Banknotes',
    href: `/products?category=Banknotes`,
  },
  {
    label: 'Jewellery',
    href: `/products?category=Jewellery`,
  },
  {
    label: 'Collectibles',
    href: `/products?category=Collectibles`,
  },
  {
    label: 'Coins',
    href: `/products?category=Coins`,
  },
  {
    label: 'Stamps',
    href: `/products?category=Stamps`,
  },
  {
    label: 'Property',
    href: `/products?category=Property`,
  },
  {
    label: 'Explore Categories',
    href: '/categories',
  },
];

export default function Header({ user }: HeaderProps) {
  const [openLoginDialog, setOpenLoginDialog] = useState(false);
  const [openLoginSellerDialog, setOpenLoginSellerDialog] = useState(false);
  const [openRegisterDialog, setOpenRegisterDialog] = useState(false);

  const location = useLocation();

  const isUser = user?.userRole?.handle === SystemRoles.User;
  const isSeller = user?.userRole?.handle === SystemRoles.Seller;
  const isAdmin = user?.userRole?.handle === SystemRoles.Admin;

  const isLoggedIn = Boolean(user);
  const username = user?.firstName ? `${user.firstName} ${user.lastName}` : user?.email || '';

  const topNavLinks = [
    {
      label: isAdmin ? 'Auctions' : 'My Auctions',
      href: '/my-auctions',
    },
    {
      label: 'Vendors',
      href: '/vendors',
      adminOnly: true,
    },
    {
      label: 'Customers',
      href: '/customers',
    },
    {
      label: 'Orders',
      href: '/orders',
    },
    {
      label: 'Settings',
      href: '/settings',
      adminOnly: true,
      sellerOnly: true,
    },
  ];

  return (
    <header className="sticky top-0 z-50 flex items-center border-b bg-muted/40 bg-white pb-3 pt-5">
      <div className="container mx-auto flex h-full w-full flex-col gap-x-8 text-black">
        <div className="flex flex-row items-center justify-between gap-x-4">
          <div className="flex flex-row items-center">
            <Link className="flex flex-row items-center" to={'/'}>
              <img src="/images/best2bid-logo.svg" className="w-10 md:w-12" />
            </Link>
            {isLoggedIn && (isAdmin || isSeller) ? (
              <div className="no-scrollbar ml-5 hidden flex-row gap-x-8 overflow-x-scroll md:flex">
                {topNavLinks
                  .filter((link) => {
                    if (isAdmin && link.adminOnly) return link;
                    if (isSeller && link.sellerOnly) return link;
                    return !link.adminOnly;
                  })
                  .map((link, index) => (
                    <React.Fragment key={index}>
                      {index === 0 && <div className="separator text-lg font-light">|</div>}
                      <Link
                        key={index}
                        to={link.href}
                        className="flex items-center gap-3 rounded-lg text-white/70 transition-all hover:text-white"
                      >
                        <span
                          key={index}
                          className={cn({
                            'font-semibold text-blue-500': location.pathname.includes(link.href),
                            'text-neutral-600': !location.pathname.includes(link.href),
                          })}
                        >
                          {link.label}
                        </span>
                      </Link>
                    </React.Fragment>
                  ))}
              </div>
            ) : null}
          </div>
          {isLoggedIn ? (
            <div className="flex flex-row items-center gap-x-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="rounded-full">
                    <div className="flex flex-row items-center gap-x-2">
                      <CircleUser className="h-5 w-5" />
                      <p className="text-black">{username}</p>
                      <ChevronDown className="h-5 w-5" />
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {/* <DropdownMenuLabel>My Account</DropdownMenuLabel> */}
                  {/* <DropdownMenuSeparator /> */}
                  {isSeller && (
                    <Link to={'/vendor-account'}>
                      <DropdownMenuItem>My Account</DropdownMenuItem>
                    </Link>
                  )}
                  {isUser && (
                    <Link to={'/my-account'}>
                      <DropdownMenuItem>My Account</DropdownMenuItem>
                    </Link>
                  )}
                  {/* <DropdownMenuItem>Support</DropdownMenuItem> */}
                  <DropdownMenuSeparator />
                  {(isAdmin || isSeller) && (
                    <>
                      <Link to={'/my-auctions'}>
                        <DropdownMenuItem>Auctions</DropdownMenuItem>
                      </Link>
                      {isAdmin && (
                        <Link to={'/vendors'}>
                          <DropdownMenuItem>Vendors</DropdownMenuItem>
                        </Link>
                      )}
                      <Link to={'/customers'}>
                        <DropdownMenuItem>Customers</DropdownMenuItem>
                      </Link>
                      <Link to={'/orders'}>
                        <DropdownMenuItem>Orders</DropdownMenuItem>
                      </Link>
                      <Link to={'/settings'}>
                        <DropdownMenuItem>Settings</DropdownMenuItem>
                      </Link>
                      <DropdownMenuSeparator />
                    </>
                  )}

                  <form method="post" action="/api/logout">
                    <DropdownMenuItem>
                      <button className="flex w-full text-red-600">Logout</button>
                    </DropdownMenuItem>
                  </form>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <>
              <div className="hidden flex-row gap-x-4 lg:flex">
                <Login
                  asSeller
                  openLoginSellerDialog={openLoginSellerDialog}
                  setOpenLoginSellerDialog={(value) => setOpenLoginSellerDialog(value)}
                />
                <div className="border" />
                <Login
                  openLoginDialog={openLoginDialog}
                  setOpenLoginDialog={(value) => setOpenLoginDialog(value)}
                  onSwitchToRegister={() => {
                    setOpenLoginDialog(false);
                    setOpenRegisterDialog(true);
                  }}
                />
                <Registration
                  openRegisterDialog={openRegisterDialog}
                  setOpenRegisterDialog={(value) => setOpenRegisterDialog(value)}
                  onSwitchToLogin={() => {
                    setOpenRegisterDialog(false);
                    setOpenLoginDialog(true);
                  }}
                />
              </div>
              <div className="flex flex-row items-center gap-x-4 lg:hidden">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="rounded-full">
                      <div className="flex flex-row items-center gap-x-2">
                        <List className="h-5 w-5" />
                      </div>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {/* <DropdownMenuLabel>My Account</DropdownMenuLabel> */}
                    <DropdownMenuItem>
                      <Login
                        asSeller
                        openLoginSellerDialog={openLoginSellerDialog}
                        setOpenLoginSellerDialog={(value) => setOpenLoginSellerDialog(value)}
                      />
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem>
                      <Login
                        openLoginDialog={openLoginDialog}
                        setOpenLoginDialog={(value) => setOpenLoginDialog(value)}
                        onSwitchToRegister={() => {
                          setOpenLoginDialog(false);
                          setOpenRegisterDialog(true);
                        }}
                      />
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <Registration
                        openRegisterDialog={openRegisterDialog}
                        setOpenRegisterDialog={(value) => setOpenRegisterDialog(value)}
                        onSwitchToLogin={() => {
                          setOpenRegisterDialog(false);
                          setOpenLoginDialog(true);
                        }}
                      />
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </>
          )}
        </div>
        {isLoggedIn && (isAdmin || isSeller) ? (
          <div className="no-scrollbar mt-3 flex flex-row gap-x-8 overflow-x-scroll md:hidden">
            {topNavLinks
              .filter((link) => {
                if (isAdmin) return link;

                return !link.adminOnly;
              })
              .map((link, index) => (
                <React.Fragment key={index}>
                  <Link
                    key={index}
                    to={link.href}
                    className="flex items-center gap-3 rounded-lg text-white/70 transition-all hover:text-white"
                  >
                    <span
                      key={index}
                      className={cn({
                        'font-semibold text-blue-500': location.pathname.includes(link.href),
                        'text-neutral-600': !location.pathname.includes(link.href),
                      })}
                    >
                      {link.label}
                    </span>
                  </Link>
                </React.Fragment>
              ))}
          </div>
        ) : null}
        <div className="no-scrollbar mt-3 flex flex-row gap-x-8 overflow-x-scroll">
          {(!isLoggedIn || isUser) &&
            categoriesLinks.map((link, index) => (
              <React.Fragment key={index}>
                {index === 1 && <div className="separator text-lg font-light">|</div>}
                <Link
                  key={index}
                  to={link.href}
                  className={cn(
                    'flex items-center gap-3 text-nowrap rounded-lg text-white/70 transition-all hover:text-white',
                    {
                      'font-normal text-white': location.pathname.includes(link.href),
                    },
                  )}
                >
                  <span key={index} className="text-neutral-600">
                    {link.label}
                  </span>
                </Link>
              </React.Fragment>
            ))}
        </div>
      </div>
    </header>
  );
}
